import React from 'react';
import { Label } from '@/components/ui/label';
import { AnimatePresence, LayoutGroup, motion } from 'framer-motion';
import { cn } from '@/lib/utils';
import { EASE_EXPO_OUT, DURATION_MEDIUM, DURATION_SLOW } from '@/lib/motion';
import { HelpButton } from '@/components/ui/help-button';
import { type LucideIcon, Lock } from 'lucide-react';
import { LedButton, LedButtonGrid } from '@/components/ui/led-button-grid';
import { Tooltip, TooltipContent, TooltipTrigger } from '@/components/ui/tooltip';
import { CustomizedFieldIndicator } from '../../../form/CustomizedFieldIndicator';
import { AnimatedFormError } from '../../../form/AnimatedFormError';
import { AnimatedCollapse } from '@/components/ui/animated-collapse';
import { fieldLabelClass, fieldDescriptionClass } from '@/components/config_forms/shared/surcharge/fieldStyles';
import { useMethodSwapAnimation } from './useMethodSwapAnimation';
import { SurchargeMethodPanel } from './SurchargeMethodPanel';

export interface SurchargeMethod {
  value: string;
  label: string;
  description: string;
  /** Alternate description when in count trigger mode. Falls back to `description` if not set. */
  countModeDescription?: string;
  icon?: LucideIcon;
  /** Short label for LedButton display. Falls back to `label` if not set. */
  buttonLabel?: string;
  /** Technical name shown as subtitle in description area. Falls back to `label` if not set. */
  technicalName?: string;
}

const MethodDescriptionContent: React.FC<{
  selectedMethod: SurchargeMethod;
  methodDescriptions?: Record<string, string>;
  getDisplayLabel: (method: SurchargeMethod) => string;
  getHelpFieldName: (name: string) => string;
  getVariables: (methodValue: string) => Record<string, unknown>;
  expandableExamples?: Record<string, React.ReactNode>;
  exampleExpanded: boolean;
  onToggleExample: () => void;
  methodInjectables?: Record<string, Record<string, React.ReactNode>>;
}> = ({
  selectedMethod,
  methodDescriptions,
  getDisplayLabel,
  getHelpFieldName,
  getVariables,
  expandableExamples,
  exampleExpanded,
  onToggleExample,
  methodInjectables,
}) => {
  return (
    <>
      {selectedMethod.technicalName && selectedMethod.technicalName !== getDisplayLabel(selectedMethod) && (
        <div className="font-label text-[9px] uppercase tracking-wide text-muted-foreground mb-1">
          {selectedMethod.technicalName}
        </div>
      )}
      <div className="text-sm text-foreground">
        <span className="font-semibold text-primary-text">
          {getDisplayLabel(selectedMethod)}
        </span>
        <span className="text-muted-foreground"> — </span>
        <span className="text-muted-foreground text-xs">{selectedMethod.description}</span>
      </div>
      {methodDescriptions?.[selectedMethod.value] && (
        <HelpButton
          variant="link"
          fieldName={getHelpFieldName(getDisplayLabel(selectedMethod))}
          content={methodDescriptions[selectedMethod.value]}
          variables={getVariables(selectedMethod.value)}
          injectableComponents={methodInjectables?.[selectedMethod.value]}
          enableFieldFocus
          className="mt-2"
        />
      )}
      {expandableExamples?.[selectedMethod.value] && (
        <>
          <button
            type="button"
            onClick={(e) => {
              e.preventDefault();
              onToggleExample();
            }}
            className="mt-2 text-xs text-primary-text font-medium hover:text-primary-text/80 transition-colors"
          >
            How it works {exampleExpanded ? '▾' : '▸'}
          </button>
          <AnimatedCollapse open={exampleExpanded}>
            <div className="mt-2 p-3 rounded-[0.25rem] border border-primary/30 bg-primary/5">
              {expandableExamples[selectedMethod.value]}
            </div>
          </AnimatedCollapse>
        </>
      )}
    </>
  );
};

interface SurchargeMethodSelectorProps {
  id: string;
  title: string;
  description?: string;
  methods: SurchargeMethod[];
  selectedValue: string;
  onValueChange: (value: string) => void;
  methodDescriptions?: Record<string, string>;
  titleTooltip?: string;
  titleTooltipInjectables?: Record<string, React.ReactNode>;
  isMethodDisabled?: (method: SurchargeMethod) => boolean;
  getDisabledTooltip?: (method: SurchargeMethod) => string;
  markdownVariables?: Record<string, unknown> | ((methodValue: string) => Record<string, unknown>);
  /** Number of grid columns — matches LedButtonGrid. Falls back to 3 on mobile. */
  columns?: 3 | 4 | 5 | 6;
  isCustomized?: boolean;
  context?: 'user' | 'client';
  errorElement?: React.ReactNode;
  titlePosition?: 'center' | 'left';
  expandableExamples?: Record<string, React.ReactNode>;
  /** Prefix prepended to help sheet field names (e.g. "Short Turn" → "Short Turn - Carry Over") */
  helpFieldNamePrefix?: string;
  /** Injectable React components for method help panels, keyed by method value. */
  methodInjectables?: Record<string, Record<string, React.ReactNode>>;
}

export const SurchargeMethodSelector: React.FC<SurchargeMethodSelectorProps> = ({
  id, title, description, methods, selectedValue, onValueChange,
  methodDescriptions, titleTooltip, titleTooltipInjectables,
  isMethodDisabled, getDisabledTooltip, markdownVariables,
  columns = 4, isCustomized, context, errorElement,
  titlePosition = 'center', expandableExamples, helpFieldNamePrefix,
  methodInjectables,
}) => {
  const selectedMethod = methods.find(m => m.value === selectedValue);
  const [exampleExpanded, setExampleExpanded] = React.useState(false);
  const {
    containerStyle: descriptionContainerStyle,
    containerRef: descriptionContainerRef,
    activePanelRef: descriptionPanelRef,
    fadeDuration: descFadeDuration,
  } = useMethodSwapAnimation(selectedValue, { observeResize: true });
  const collapseExample = () => setExampleExpanded(false);

  React.useEffect(() => {
    setExampleExpanded(false);
  }, [selectedValue]);
  const getVariables = (methodValue: string) =>
    typeof markdownVariables === 'function' ? markdownVariables(methodValue) : markdownVariables || {};
  const getDisplayLabel = (m: SurchargeMethod) => m.buttonLabel ?? m.label;
  const getHelpFieldName = (name: string) => helpFieldNamePrefix ? `${helpFieldNamePrefix} - ${name}` : name;
  const titleTooltipNode = titleTooltip ? (
    <HelpButton fieldName={getHelpFieldName(title)} content={titleTooltip} injectableComponents={titleTooltipInjectables} />
  ) : null;

  const selectedBadge = selectedMethod ? (
    <span className="inline-flex h-6 items-center">
      <AnimatePresence mode="wait" initial={false}>
        <motion.span
          key={selectedValue}
          initial={{ opacity: 0, scale: 0.8 }}
          animate={{ opacity: 1, scale: 1 }}
          exit={{ opacity: 0, scale: 0.8 }}
          transition={{ duration: DURATION_MEDIUM, ease: EASE_EXPO_OUT }}
          className="inline-flex whitespace-nowrap px-2 py-1 bg-primary/10 text-primary-text rounded text-xs font-bold"
        >
          {getDisplayLabel(selectedMethod)}
        </motion.span>
      </AnimatePresence>
    </span>
  ) : null;

  const renderGrid = () => (
    <>
      <LayoutGroup id={`${id}-grid`}>
        <LedButtonGrid
          columns={columns}
          role="radiogroup"
          aria-label={title}
          id={id}
          data-validation-key={id}
        >
          {[...methods].sort((a, b) => {
            const aDisabled = isMethodDisabled?.(a) ?? false;
            const bDisabled = isMethodDisabled?.(b) ?? false;
            return Number(aDisabled) - Number(bDisabled);
          }).map((method) => {
            const isSelected = selectedValue === method.value;
            const isDisabled = isMethodDisabled?.(method) ?? false;
            const disabledReason = isDisabled && getDisabledTooltip
              ? getDisabledTooltip(method)
              : undefined;
            const displayLabel = getDisplayLabel(method);

            // Don't pass `disabled` to LedButton — it adds `pointer-events-none`
            // which prevents tooltip hover on disabled methods. Use className-only styling.
            const button = (
              <LedButton
                color={isDisabled ? 'empty' : 'amber'}
                active={isSelected && !isDisabled}
                icon={method.icon ? <method.icon className="h-[22px] w-[22px]" /> : null}
                label={displayLabel}
                role="radio"
                aria-checked={isSelected}
                aria-disabled={isDisabled}
                onClick={() => {
                  if (!isDisabled) {
                    if (method.value !== selectedValue) {
                      (window as unknown as Record<string, unknown>).__exitAlreadyPlayed = true;
                    }
                    onValueChange(method.value);
                    collapseExample();
                  }
                }}
                className={cn(
                  isDisabled && 'opacity-40 cursor-not-allowed',
                  isSelected && !isDisabled && 'ring-1 ring-primary/50'
                )}
              />
            );

            if (isDisabled && disabledReason) {
              return (
                <motion.div key={method.value} layout transition={{ duration: DURATION_SLOW, ease: EASE_EXPO_OUT }}>
                  <Tooltip>
                    <TooltipTrigger asChild>
                      <div className="relative">
                        {button}
                        <Lock className="absolute top-1 right-1 h-3 w-3 text-muted-foreground/70" />
                      </div>
                    </TooltipTrigger>
                    <TooltipContent side="top" className="text-xs max-w-[200px]">
                      {disabledReason}
                    </TooltipContent>
                  </Tooltip>
                </motion.div>
              );
            }

            return (
              <motion.div key={method.value} layout transition={{ duration: DURATION_SLOW, ease: EASE_EXPO_OUT }}>
                {button}
              </motion.div>
            );
          })}
        </LedButtonGrid>
      </LayoutGroup>

      {/* Description area — crossfade via SurchargeMethodPanel, height animated via useMethodSwapAnimation */}
      {selectedMethod && (
        <div
          ref={descriptionContainerRef}
          className="overflow-hidden my-4"
          style={descriptionContainerStyle}
          data-anim-audit="method-description-container"
        >
          <div className="relative grid">
            <AnimatePresence mode="sync" initial={false}>
              <SurchargeMethodPanel
                key={selectedMethod.value}
                activePanelRef={descriptionPanelRef}
                transitionDuration={descFadeDuration}
              >
                <div className="border border-border border-l-2 border-l-primary rounded bg-surface-1 p-3">
                  <MethodDescriptionContent
                    selectedMethod={selectedMethod}
                    methodDescriptions={methodDescriptions}
                    getDisplayLabel={getDisplayLabel}
                    getHelpFieldName={getHelpFieldName}
                    getVariables={getVariables}
                    expandableExamples={expandableExamples}
                    exampleExpanded={exampleExpanded}
                    onToggleExample={() => setExampleExpanded(prev => !prev)}
                    methodInjectables={methodInjectables}
                  />
                </div>
              </SurchargeMethodPanel>
            </AnimatePresence>
          </div>
        </div>
      )}
    </>
  );

  return (
    <div className="space-y-4">
      {titlePosition === 'center' ? (
        <div className="space-y-1 text-center">
          <div className="flex items-center justify-center gap-2">
            <Label className={cn(fieldLabelClass, "whitespace-nowrap")}>
              {title}
            </Label>
            {selectedBadge}
            {titleTooltipNode}
            <CustomizedFieldIndicator isCustomized={isCustomized} context={context} />
          </div>
          {description && (
            <p className={cn(fieldDescriptionClass, "mt-1")}>
              {description}
            </p>
          )}
        </div>
      ) : (
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-4 lg:items-start">
          <div className="lg:col-span-1 space-y-1">
            <div className="flex items-center gap-2">
              <Label className={cn(fieldLabelClass, "whitespace-nowrap")}>
                {title}
              </Label>
              {selectedBadge}
              {titleTooltipNode}
              <CustomizedFieldIndicator isCustomized={isCustomized} context={context} />
            </div>
            {description && (
              <p className={fieldDescriptionClass}>
                {description}
              </p>
            )}
          </div>
          <div className="lg:col-span-2">
            {renderGrid()}
          </div>
        </div>
      )}
      {titlePosition === 'center' && renderGrid()}
      {errorElement && (
        <AnimatedFormError className="mt-1 text-center">
          {errorElement}
        </AnimatedFormError>
      )}
    </div>
  );
};
